/*  useDailyLog — unified reader/writer for the daily log.
    -----------------------------------------------------------------
    One shared hook for every surface that shows daily food + workout
    + weight:  Interactive Kitchen · Interactive Gym · Tracker Calendar ·
    Daily / Log Calendar · AI Coach.  Kitchen cook / eat / food-photo /
    gym session-complete already write into the same daily-log doc
    server-side, so ALL surfaces automatically stay in sync — even
    across tabs (via the window "daily-log:changed" event).

    Usage:
      const { day, loading, refresh,
              addFood, addWorkout, setWeight,
              patchFood, patchWorkout, deleteFood, deleteWorkout } =
        useDailyLog();          // today

      const { day: aug5, refresh } = useDailyLog("2026-08-05");  // any date
*/
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_DAY = {
  foods: [], workouts: [], weight_lbs: null,
  totals: { kcal_in: 0, protein_g: 0, carbs_g: 0, fat_g: 0, kcal_out: 0, workout_min: 0 },
};

const BROADCAST = "daily-log:changed";

function broadcast(datePt) {
  try {
    window.dispatchEvent(new CustomEvent(BROADCAST, { detail: { date_pt: datePt } }));
  } catch (_) { /* noop */ }
}

export default function useDailyLog(datePt) {
  const [day, setDay]         = useState(EMPTY_DAY);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const url = datePt ? `${API}/daily-log?date=${datePt}` : `${API}/daily-log`;
      const { data } = await axios.get(url, { withCredentials: true });
      setDay({ ...EMPTY_DAY, ...data });
    } catch (_) {
      setDay(EMPTY_DAY);
    } finally {
      setLoading(false);
    }
  }, [datePt]);

  useEffect(() => { load(); }, [load]);

  // Cross-component sync — any add/edit/delete anywhere else fires this event.
  useEffect(() => {
    const onChange = (ev) => {
      // Only react if broadcast matches our date (or if no date filter set)
      if (!datePt || (ev?.detail?.date_pt || null) === datePt) load();
    };
    window.addEventListener(BROADCAST, onChange);
    return () => window.removeEventListener(BROADCAST, onChange);
  }, [datePt, load]);

  const _apply = useCallback((data) => {
    if (data?.day) setDay({ ...EMPTY_DAY, ...data.day });
    broadcast(data?.day?.date_pt || datePt);
  }, [datePt]);

  const addFood = useCallback(async (payload) => {
    const { data } = await axios.post(
      `${API}/daily-log/food`,
      { ...payload, date_pt: datePt || undefined },
      { withCredentials: true },
    );
    _apply(data);
    return data;
  }, [datePt, _apply]);

  const addWorkout = useCallback(async (payload) => {
    const { data } = await axios.post(
      `${API}/daily-log/workout`,
      { ...payload, date_pt: datePt || undefined },
      { withCredentials: true },
    );
    _apply(data);
    return data;
  }, [datePt, _apply]);

  const setWeight = useCallback(async (weight_lbs) => {
    const { data } = await axios.post(
      `${API}/daily-log/weight`,
      { weight_lbs: Number(weight_lbs), date_pt: datePt || undefined },
      { withCredentials: true },
    );
    _apply(data);
    return data;
  }, [datePt, _apply]);

  const patchFood = useCallback(async (entry_id, patch) => {
    const d = day?.date_pt || datePt;
    if (!d) return null;
    const { data } = await axios.patch(
      `${API}/daily-log/${d}/food/${entry_id}`, patch,
      { withCredentials: true },
    );
    _apply(data);
    return data;
  }, [day, datePt, _apply]);

  const patchWorkout = useCallback(async (entry_id, patch) => {
    const d = day?.date_pt || datePt;
    if (!d) return null;
    const { data } = await axios.patch(
      `${API}/daily-log/${d}/workout/${entry_id}`, patch,
      { withCredentials: true },
    );
    _apply(data);
    return data;
  }, [day, datePt, _apply]);

  const deleteFood = useCallback(async (entry_id) => {
    const d = day?.date_pt || datePt;
    if (!d) return null;
    const { data } = await axios.delete(
      `${API}/daily-log/${d}/food/${entry_id}`,
      { withCredentials: true },
    );
    _apply(data);
    return data;
  }, [day, datePt, _apply]);

  const deleteWorkout = useCallback(async (entry_id) => {
    const d = day?.date_pt || datePt;
    if (!d) return null;
    const { data } = await axios.delete(
      `${API}/daily-log/${d}/workout/${entry_id}`,
      { withCredentials: true },
    );
    _apply(data);
    return data;
  }, [day, datePt, _apply]);

  return {
    day, loading, refresh: load,
    addFood, addWorkout, setWeight,
    patchFood, patchWorkout, deleteFood, deleteWorkout,
  };
}

export function useDailyLogRange(startDatePt, endDatePt) {
  const [days, setDays]       = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!startDatePt || !endDatePt) { setDays([]); setLoading(false); return; }
    try {
      const { data } = await axios.get(
        `${API}/daily-log/range?start=${startDatePt}&end=${endDatePt}`,
        { withCredentials: true },
      );
      setDays(data?.days || []);
    } catch (_) {
      setDays([]);
    } finally {
      setLoading(false);
    }
  }, [startDatePt, endDatePt]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onChange = () => load();
    window.addEventListener(BROADCAST, onChange);
    return () => window.removeEventListener(BROADCAST, onChange);
  }, [load]);

  return { days, loading, refresh: load };
}

export { BROADCAST as DAILY_LOG_EVENT };
